import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import teamAPI from "../../../services/teamApi";
import clinicAPI from "../../../services/clinicApi";
import { hasAccessToken } from "../../../utils/authStorage";
import { getClinicPortalLinks } from "../../../utils/clinicPortalLinks";
import {
  firstFieldError,
  validateTrainerInviteForm,
} from "../../../utils/trainerFormValidation";
import { isClubOwner } from "../../../utils/roles";
import { usePlanAccess } from "../../../hooks/usePlanAccess";
import { HubFormCard } from "../AdminPortal/HubPwa";
import TrainerAssignmentsFields, { toggleId } from "./TrainerAssignmentsFields";
import aa from "../AdminPortal/adminApp.module.css";
import {
  Button,
  Field,
  Input,
  PhoneInput,
  Segment,
  Textarea,
  useToast,
} from "../../ui";

const EMPTY_FORM = {
  role: "trainer",
  fullName: "",
  phone: "",
  email: "",
  password: "",
  sendInvite: true,
  description: "",
  groupIds: [],
};

const ROLE_OPTIONS = [
  { id: "trainer", label: "Тренер" },
  { id: "admin", label: "Админ" },
];

export default function TeamInviteForm({
  onSuccess,
  onCancel,
  sheet = false,
  formId,
  onLoadingChange,
}) {
  const toast = useToast();
  const { tenant, role, profile, club } = useSelector((state) => state.auth);
  const plan = usePlanAccess();
  const canInviteAdmin =
    plan.adminRoles && isClubOwner({ role, profileRole: profile?.role, club });

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const staffPortalUrl =
    getClinicPortalLinks(tenant?.subdomain).find((l) => l.id === "staff")?.url ||
    "";
  const adminPortalUrl =
    getClinicPortalLinks(tenant?.subdomain).find((l) => l.id === "admin")?.url ||
    "";

  useEffect(() => {
    setForm(EMPTY_FORM);
    clinicAPI
      .getSpecializations()
      .then((r) =>
        setGroups((r.specializations || []).filter((x) => x.is_active !== false)),
      )
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    if (!canInviteAdmin && form.role === "admin") {
      setForm((f) => ({ ...f, role: "trainer" }));
    }
  }, [canInviteAdmin, form.role]);

  const patch = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setFieldErrors((e) => ({ ...e, [key]: undefined }));
  };

  const isAdmin = form.role === "admin";

  const submit = async (e) => {
    e.preventDefault();
    if (!hasAccessToken()) {
      toast.error("Сессия истекла. Войдите снова.");
      return;
    }
    if (isAdmin && !canInviteAdmin) {
      toast.error("Недостаточно прав для приглашения администратора");
      return;
    }
    const errors = validateTrainerInviteForm(form, {
      requirePassword: !form.sendInvite,
    });
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      toast.error(firstFieldError(errors));
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const payload = {
        role: isAdmin ? "admin" : "trainer",
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        fullName: form.fullName.trim(),
        password: form.sendInvite ? undefined : form.password,
        send_invite: form.sendInvite,
      };
      if (!isAdmin) {
        payload.description = form.description?.trim() || undefined;
        payload.groupIds = form.groupIds;
      }

      const res = await teamAPI.invite(payload);

      if (res?.result !== 0) {
        toast.error(res?.error || "Не удалось пригласить");
        return;
      }

      const inviteUrl = res.invite_url || res.devInviteUrl;
      if (inviteUrl && !res.email_sent) {
        try {
          await navigator.clipboard.writeText(inviteUrl);
          toast.success(
            res.invite_note || "Ссылка приглашения скопирована",
            8000,
          );
        } catch {
          toast.success(res.message || "Сотрудник добавлен", 8000);
        }
      } else {
        toast.success(res.message || "Сотрудник добавлен", 8000);
      }
      onSuccess?.(res);
    } catch (err) {
      const msg = err.response?.data?.error || "Ошибка";
      toast.error(msg);
      if (/email/i.test(msg)) setFieldErrors((prev) => ({ ...prev, email: msg }));
      else if (/телефон/i.test(msg)) setFieldErrors((prev) => ({ ...prev, phone: msg }));
      else if (/парол/i.test(msg)) setFieldErrors((prev) => ({ ...prev, password: msg }));
      else if (/фио|имя/i.test(msg)) setFieldErrors((prev) => ({ ...prev, fullName: msg }));
    } finally {
      setLoading(false);
    }
  };

  const inviteToggleLabel = "Отправить приглашение на email";
  const inviteToggleHint = "Ссылка для входа придёт автоматически";

  const inviteBlock = !form.sendInvite ? (
    <Field label="Пароль" hint="Минимум 8 символов" error={fieldErrors.password}>
      <Input
        type="password"
        required={!form.sendInvite}
        minLength={8}
        value={form.password}
        error={fieldErrors.password}
        onChange={(e) => patch("password", e.target.value)}
      />
    </Field>
  ) : (
    <p className={aa.teamInviteHintBox}>
      Код клуба: <strong>{tenant?.subdomain || "—"}</strong>
      {isAdmin && adminPortalUrl ? (
        <>
          {" "}
          · портал:{" "}
          <a href={adminPortalUrl} target="_blank" rel="noreferrer">
            {adminPortalUrl.replace(/^https?:\/\//, "")}
          </a>
        </>
      ) : null}
      {!isAdmin && staffPortalUrl ? (
        <>
          {" "}
          · портал:{" "}
          <a href={staffPortalUrl} target="_blank" rel="noreferrer">
            {staffPortalUrl.replace(/^https?:\/\//, "")}
          </a>
        </>
      ) : null}
    </p>
  );

  const useCardPickers = sheet || !formId;

  return (
    <form id={formId} onSubmit={submit} className={aa.teamInviteForm}>
      {canInviteAdmin ? (
        <HubFormCard title="Роль">
          <Segment
            options={ROLE_OPTIONS}
            value={form.role}
            onChange={(id) => patch("role", id)}
          />
        </HubFormCard>
      ) : null}

      <HubFormCard title={isAdmin ? "Администратор" : "Тренер"}>
        <Field label="ФИО" error={fieldErrors.fullName}>
          <Input
            required
            value={form.fullName}
            error={fieldErrors.fullName}
            onChange={(e) => patch("fullName", e.target.value)}
            placeholder="Иванов Иван"
            autoFocus={!formId}
          />
        </Field>

        <Field
          label="Email"
          hint="Основной идентификатор для входа в портал"
          error={fieldErrors.email}
        >
          <Input
            type="email"
            required
            value={form.email}
            error={fieldErrors.email}
            onChange={(e) => patch("email", e.target.value)}
            placeholder="email@example.com"
          />
        </Field>

        <Field
          label="Телефон"
          hint="Необязательно — для звонков и SMS"
          error={fieldErrors.phone}
        >
          <PhoneInput
            value={form.phone}
            error={fieldErrors.phone}
            onChange={(v) => patch("phone", v)}
          />
        </Field>

        <label className={aa.teamInviteToggleCard}>
          <input
            type="checkbox"
            checked={form.sendInvite}
            onChange={(e) =>
              setForm({ ...form, sendInvite: e.target.checked })
            }
          />
          <span className={aa.teamInviteToggleText}>
            <strong>{inviteToggleLabel}</strong>
            <span>{inviteToggleHint}</span>
          </span>
        </label>

        {inviteBlock}
      </HubFormCard>

      {!isAdmin ? (
        <>
          <HubFormCard title="Группы">
            <p className={aa.hubHint}>
              Необязательно — можно назначить позже в профиле тренера.
            </p>
            <TrainerAssignmentsFields
              sheet={useCardPickers}
              hideSectionTitle
              groupsOnly
              optional
              groups={groups}
              groupIds={form.groupIds}
              onToggleGroup={(id) =>
                setForm((f) => ({
                  ...f,
                  groupIds: toggleId(f.groupIds, id),
                }))
              }
            />
          </HubFormCard>

          <HubFormCard title="Дополнительно">
            <Field label="Описание">
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Необязательно"
              />
            </Field>
          </HubFormCard>
        </>
      ) : null}

      {!formId ? (
        <div className={aa.teamInviteFormFoot}>
          <Button type="submit" disabled={loading}>
            {loading ? "Отправка…" : "Пригласить"}
          </Button>
          {onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Отмена
            </Button>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
